import { observer } from 'mobx-react';
import { Fragment, useEffect, useState } from 'react';
import { runInAction } from 'mobx';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { Textarea } from '@monorepo/base/src/components/form/textarea/textarea';
import { IAdminxStore } from '../../../../stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import styles from './bidder-whitelist-blacklist.module.scss';
import { Spacer } from '@monorepo/base/src/components/spacer/spacer';
import { ButtonDimensions, PrimaryButton as BadIspApplyButton } from '@monorepo/base/src/components/buttons/buttons';
import { BidderApi } from '../../../../apis/bidder.api';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { ActionModal, ModalTypes } from '@monorepo/controlled/src/components/modal/modals/action-modal/action-modal';
import { BidderCampaignModel } from '../../../../models/bidder-campaign/bidder-camapign.model';
import { normalizeBadIspText, sanitizeBadIspWordsDraft } from '../../../../utils/bad-isp-words';

function hydrateDraftFromCampaign(campaign: BidderCampaignModel) {
	const raw = campaign.bad_isp_words ?? '';
	const normalized = sanitizeBadIspWordsDraft(raw) || undefined;
	if (normalized !== raw) {
		campaign.bad_isp_words = normalized;
	}
	return normalized ?? '';
}

export const BidderCampaignBadIspWords = observer(() => {
	const { bidderStore, modalsStore } = useStores<IAdminxStore>();
	const { dispatchLog } = useLogs();
	const [isSavingBadIsp, setIsSavingBadIsp] = useState(false);
	const [draft, setDraft] = useState('');

	const campaignCrudStore = bidderStore.getCrud();
	const campaign = campaignCrudStore.getData().campaign;
	const originCampaign = campaignCrudStore.getOriginData().campaign;

	useEffect(() => {
		if (campaign === undefined) {
			setDraft('');
			return;
		}
		setDraft(hydrateDraftFromCampaign(campaign));
	}, [campaign, campaign?.campaign_id]);

	if (campaign === undefined) {
		return null;
	}

	const isBadIspDirty =
		!!originCampaign &&
		!!campaign.campaign_id &&
		normalizeBadIspText(campaign.bad_isp_words) !== normalizeBadIspText(originCampaign.bad_isp_words);

	return (
		<Fragment>
			<Spacer />
			<div id="badIspWords">
				<Textarea
					value={draft}
					placeholder={'Bad ISP words'}
					rows={4}
					onValue={value => {
						const next = sanitizeBadIspWordsDraft(value) || undefined;
						campaign.bad_isp_words = next;
						setDraft(next ?? '');
					}}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'bad_isp_words')] }}
				/>
				{isBadIspDirty ? (
					<Fragment>
						<Spacer />
						<div className={styles.applyBadIspButtonRow}>
							<BadIspApplyButton
								className={styles.applyBadIspButton}
								dimension={ButtonDimensions.Small}
								disabled={isSavingBadIsp}
								isRotateIcon={isSavingBadIsp}
								icon={isSavingBadIsp ? 'loading-01' : undefined}
								onClick={async () => {
									if (!campaign.campaign_id) {
										return;
									}
									const bad_isp_words = normalizeBadIspText(campaign.bad_isp_words);
									setIsSavingBadIsp(true);
									try {
										await BidderApi.setBadIspWordsPerCampaign({
											campaign_bad_isp_words: {
												campaign_id: campaign.campaign_id,
												bad_isp_words,
											},
										});
										runInAction(() => {
											const d = campaignCrudStore.getData().campaign;
											const o = campaignCrudStore.getOriginData().campaign;
											const next = bad_isp_words.length ? bad_isp_words : undefined;
											if (d) {
												d.bad_isp_words = next;
											}
											if (o) {
												o.bad_isp_words = next;
											}
										});
										setDraft(bad_isp_words.length ? bad_isp_words : '');
										dispatchLog({
											msg: 'Bad ISP words updated',
											type: DispatchLogsTypes.Success,
										});
									} catch (e: any) {
										modalsStore.addModal({
											children: (
												<ActionModal type={ModalTypes.Error} primaryBtnTitle={'OK'}>
													<div>
														<h2>Failed to update bad ISP words</h2>
														{e.data?.errors?.map((err: { field: string; message: string }) => (
															<div key={err.field}>{`${err.field}: ${err.message}`}</div>
														))}
													</div>
												</ActionModal>
											),
										});
									} finally {
										setIsSavingBadIsp(false);
									}
								}}>
								Apply bad ISP words
							</BadIspApplyButton>
						</div>
					</Fragment>
				) : null}
			</div>
		</Fragment>
	);
});
